import { App, Menu } from "electron";
import os from "os";
import { getOrCreateMainLogger } from "../logger/main";
import { ExternalApplicationManager } from "./ExternalApplicationManager";
import { permissionsManager } from "./PermissionsManager";
import { MainSystem } from "./MainSystem";
import { createWindow } from "./MainWindow";
import { SplashScreen } from "./SplashScreen";
import { setupListeners } from "./listeners";
import { initializePreloads } from "./preloads";
import { Manifest, Metric } from "../types/types";
import { ProtocolHandler } from "./protocolHandler";
import { DownloadManager } from "./DownloadManager";
import { startIAC } from "./IAC";
import { appDataAssetsPath } from "./appDataFolder";
import { AffinityManager } from "./AffinityGroupManager";
import { tryCatchNotify } from "../util";
import MonitorInfo from "./MonitorInfo";

const DEFAULT_RESOURCE_USAGE_LOG_INTERVAL = 60e3;

const logger = getOrCreateMainLogger();

const VIEW_MENU_FEA_CONFIG_TO_ROLE_MAP: { [key: string]: "reload" | "forceReload" | "toggleDevTools" } = {
	reloadWindowHotkey: "reload",
	reloadWindowIgnoringCacheHotkey: "forceReload",
	toggleInspectorHotkey: "toggleDevTools",
};

/**
 * The const MODIFIERS, KEY_CODES and the isValidAccelerator should be removed in a future version
 * when we redesign hotkeys API and provide UI for user to reassign hotkeys registered by apps/UX components
 */
const MODIFIERS = /^(Command|Cmd|Control|Ctrl|CommandOrControl|CmdOrCtrl|Alt|Option|AltGr|Shift|Super)$/;
const KEY_CODES =
	/^([0-9A-Z)!@#$%^&*(:+<_>?~{|}";=,\-./`[\\\]']|F1*[1-9]|F10|F2[0-4]|Plus|Space|Tab|Backspace|Delete|Insert|Return|Enter|Up|Down|Left|Right|Home|End|PageUp|PageDown|Escape|Esc|VolumeUp|VolumeDown|VolumeMute|MediaNextTrack|MediaPreviousTrack|MediaStop|MediaPlayPause|PrintScreen)$/;

const isValidAccelerator = (acceleratorString: string) => {
	const parts = acceleratorString.split("+");
	let keyFound = false;
	return parts.every((val, index) => {
		const isKey = KEY_CODES.test(val);
		const isModifier = MODIFIERS.test(val);
		if (isKey) {
			// Key must be unique
			if (keyFound) return false;
			keyFound = true;
		}
		// Key is required
		if (index === parts.length - 1 && !keyFound) return false;
		return isKey || isModifier;
	});
};

const setupMenu = (name: string, feaConfig?: any) => {
	const template: (Electron.MenuItemConstructorOptions | Electron.MenuItem)[] = [
		{
			label: "Finsemble",
			submenu: [
				{
					label: `About ${name}`,
					role: "about",
				},
			],
		},
		{
			label: "Edit",
			submenu: [{ role: "cut" }, { role: "copy" }, { role: "paste" }, { role: "delete" }, { role: "selectAll" }],
		},
	];

	const viewSubmenu: Electron.MenuItemConstructorOptions[] = [];

	Object.keys(VIEW_MENU_FEA_CONFIG_TO_ROLE_MAP).forEach((key) => {
		if (typeof feaConfig[key] === "string" || typeof feaConfig[key] === "undefined" || feaConfig[key] === true) {
			const item: Electron.MenuItemConstructorOptions | Electron.MenuItem = {
				role: VIEW_MENU_FEA_CONFIG_TO_ROLE_MAP[key],
			};
			if (typeof feaConfig[key] === "string") {
				if (isValidAccelerator(feaConfig[key])) {
					item.accelerator = feaConfig[key];
				} else {
					logger.error(`Invalid hotkey ${feaConfig[key]} for ${key} - will use Electron default`);
				}
			}
			viewSubmenu.push(item);
		}
	});

	// if we have at least one submenu item then add the menu to the template
	if (viewSubmenu.length > 0) {
		template.push({
			label: "View",
			submenu: viewSubmenu,
		});
	}

	const menu = Menu.buildFromTemplate(template);
	Menu.setApplicationMenu(menu);
};

/**
 * Optionally pop up a toast when logger error messages are received and FEA is running in dev mode
 */
const maybeDisplayLoggerErrors = (manifest: Manifest, system: MainSystem) => {
	logger.setErrorCallback((...messages: any[]) => {
		if (manifest.finsemble?.logger?.allowNativeErrorNotifications) {
			system.toast(
				{
					title: messages[1] || manifest.startup_app.name || "Finsemble",
					body: messages[0] || "No notification content?",
				},
				{
					devModeOnly: true,
				}
			);
		}
		if (process.env.ELECTRON_DEV) {
			console.error(...messages);
		}
	});
};

/**
 * Polls MainSystem.GetProcessList and logs for resource consumption troubleshooting
 */
const maybePollForResourceUsage = (resourceUsageLogInterval: number | undefined, system: MainSystem) => {
	if (resourceUsageLogInterval === undefined) resourceUsageLogInterval = DEFAULT_RESOURCE_USAGE_LOG_INTERVAL;

	if (resourceUsageLogInterval >= 0) {
		// Log initially, 15 seconds into boot in order to catch potential boot resource issues
		setTimeout(() => {
			tryCatchNotify(
				() =>
					system.getProcessList({
						respond: (data: Metric[]) => {
							logger.debug(["PollForResourceUsage:", ...data]);
						},
					}),
				undefined,
				logger
			);
		}, 15000);

		// And then every X milliseconds
		setInterval(() => {
			tryCatchNotify(
				() =>
					system.getProcessList({
						respond: (data: Metric[]) => {
							logger.debug(["PollForResourceUsage:", ...data]);
						},
					}),
				undefined,
				logger
			);
		}, resourceUsageLogInterval);
	}
};

let relaunchCount: number = 0;

export const main = async ({ app, manifest }: { app: App; manifest: Manifest }): Promise<string | null> => {
	const { appAssets, startup_app } = manifest;
	const { name: startupAppName, applicationIcon } = startup_app;

	const splashScreen = new SplashScreen();
	const protocolHandler = new ProtocolHandler(app);
	const downloadManager = new DownloadManager();
	const externalApplicationManager = new ExternalApplicationManager(
		manifest.externalAssets?.assetsFolder ?? appDataAssetsPath()
	);
	AffinityManager.setSurrogateUrl(manifest.affinitySurrogateUrl);
	const system = new MainSystem(app, manifest, externalApplicationManager, protocolHandler);

	maybeDisplayLoggerErrors(manifest, system);

	const feaConfig = manifest["finsemble-electron-adapter"];
	if (feaConfig) {
		// Set the minimum logging level
		const { logLevel } = feaConfig.logger;
		feaConfig.logger.transports.console.enable && logger.enableConsole();
		logLevel && logger.setLevel(logLevel);
		logger.setupLoggingToCentralLogger(feaConfig.logger?.transmitToCentralLogger);

		maybePollForResourceUsage(feaConfig?.logger?.resourceUsageLogInterval, system);
	}

	/**
	 * Ensure that listeners are initialized right away in order to avoid race conditions with
	 * the rest of the startup process.
	 */
	setupListeners(app, system, manifest, protocolHandler, downloadManager, splashScreen);

	const isRelaunching = app.commandLine?.hasSwitch(`relaunch${relaunchCount}`);
	const isOnlyInstance = app.requestSingleInstanceLock();

	/**
	 * If Finsemble is already running, stop processing. If Finsemble is being relaunched,
	 * it is possible that the previous exit has not yet happened. In this case startup should proceed.
	 */
	if (!isRelaunching && !isOnlyInstance) {
		logger.log("Preventing second instance of app from launching");
		app.quit();

		// returning an error message here will cause a dialog to be shown
		// (see`start` function in packages\electron-adapter\src\app.ts).
		return null;
	}

	protocolHandler.handleFirstInstance();

	/**
	 * Launching the app by right clicking on the taskbar icon sends the same command line arguments
	 * so make sure that we need something different every restart
	 * otherwise a second instance of Finsemble will launch and both instances fail to function properly.
	 */
	relaunchCount++;
	splashScreen.maybeShowSplashScreen(manifest);

	system.downloadApplicationIcon();

	logger.log(`System information:
	Electron version: ${process.versions.electron}
	FEA version: ${app.getVersion()}
	Architecture: ${os.arch()}
	Memory: ${os.totalmem()}
	Name: ${os.type()}
	OS Version: ${os.release()}
	CPUs: ${JSON.stringify(os.cpus(), null, "\t")}
	Monitor Info: ${JSON.stringify(await new Promise((resolve) => MonitorInfo.getMonitorInfo(resolve)), null, "\t")}`);

	// https://docs.microsoft.com/en-us/windows/desktop/shell/appids
	app.setAppUserModelId(startupAppName || "e2o");

	logger.log("APPLICATION LIFECYCLE: Preparing to download assets");
	const downloadAppAssetsError = await externalApplicationManager.downloadAssets(appAssets);
	if (downloadAppAssetsError.err) {
		logger.error(`downloadAppAssetsError ${downloadAppAssetsError.err}`);
	} else {
		logger.log(
			`downloadAppAssets completed successfully for:\n${JSON.stringify(downloadAppAssetsError.data, null, "\t")}`
		);
	}

	startIAC(manifest);
	await initializePreloads(manifest);
	logger.log("APPLICATION LIFECYCLE: Default preloads downloaded");

	// set default permissions on the PermissionsManager
	permissionsManager.setDefaultPermissions(manifest.finsemble.permissions);

	logger.log(`APPLICATION LIFECYCLE: Starting main application ${startupAppName}`);

	const { err } = await createWindow(
		{
			...startup_app,
			name: "System Manager",
			icon: applicationIcon,
			frame: true,
			affinity: "Finsemble",
		},
		manifest
	);
	if (err) {
		const error = `Failed to start main application ${err}`;
		logger.error(error);
		return error;
	} else {
		logger.log("APPLICATION LIFECYCLE: Main application started.");
		// This prevents Electron from closing when the splash screen closes
		system.setMainApplicationStarted();
	}

	setupMenu(startupAppName, feaConfig);

	return null;
};
